import type { FunctionComponent } from 'react';
import { useIntl } from 'react-intl';

import { ContextLink } from '../../../shared/components/context-link/context-link';
import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';
import { useWalletTotals } from '../../hooks/use-wallet-totals';
import type { IWallet } from '../../store/account.store';
import { CurrencySelector } from '../currency-selector/currency-selector';

import styles from './balance-breakdown.module.scss';

const style = createStyleHelper(styles, 'balance-breakdown');

export interface IBalanceBreakdownProps {
    className?: string;
    currencies?: string[];
    wallets?: IWallet[];
    currentCurrency: string;
    onCurrencyChange: (currency: string) => void;
}

const SHOWN_WALLETS = [
    {
        key: 'locked' as const,
        translationKey: 'top-navigation.balance.locked',
        defaultDisplayName: 'Locked',
        muted: true,
        link: '',
    },
    {
        key: 'withdrawable' as const,
        translationKey: 'top-navigation.balance.withdrawable',
        defaultDisplayName: 'Withdrawable',
        muted: true,
        link: '',
    },
    {
        key: 'bonus' as const,
        translationKey: 'top-navigation.balance.bonus',
        defaultDisplayName: 'Bonus',
        muted: true,
        link: 'account/rewards/bonuses',
    },
    {
        key: 'total' as const,
        translationKey: 'top-navigation.balance.total',
        defaultDisplayName: 'Total',
        muted: false,
        link: '',
    },
] as const;

export const BalanceBreakdownComponent: FunctionComponent<IBalanceBreakdownProps> = ({
    className,
    wallets,
    currencies = [],
    currentCurrency,
    onCurrencyChange,
}) => {
    const intl = useIntl();
    const totals = useWalletTotals(wallets);

    return (
        <div className={createClassName([className, style()])}>
            <div className={style('wrapper')}>
                {currencies.length > 1 ? (
                    <div className={style('section')}>
                        <div className={style('name')}>
                            <FormattedMessage
                                id={'top-navigation.balance.currency'}
                                defaultMessage={'Currency'}
                            />
                        </div>
                        <CurrencySelector
                            currencies={currencies}
                            value={currentCurrency}
                            onCurrencyChange={onCurrencyChange}
                        />
                    </div>
                ) : null}

                {SHOWN_WALLETS.map((wallet) => {
                    const item = totals[wallet.key];
                    return (
                        <ContextLink
                            key={wallet.key}
                            to={wallet.link ? wallet.link : ''}
                            className={style(wallet.link ? 'link' : 'normal')}
                        >
                            <div className={style('section')}>
                                <div className={style('name', { muted: wallet.muted })}>
                                    <FormattedMessage
                                        id={wallet.translationKey}
                                        defaultMessage={wallet.defaultDisplayName}
                                    />
                                </div>
                                <div
                                    className={style('balance', {
                                        muted: wallet.muted,
                                        link: !!wallet.link,
                                    })}
                                >
                                    {item?.currency
                                        ? intl.formatNumber(item.value, {
                                              style: 'currency',
                                              currency: item?.currency || '',
                                          })
                                        : ''}
                                </div>
                            </div>
                        </ContextLink>
                    );
                })}
            </div>
        </div>
    );
};
